import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  answerLetterCount,
  answersMatch,
  joinSlotValues,
} from "./unscrambleUtils";

const UnscrambleRow = forwardRef(function UnscrambleRow({
  index,
  scrambled,
  answer,
  clue,
  showClueButton,
  done,
  onSolved,
  onShowClue,
  textStyle,
}, ref) {
  const slotCount = useMemo(() => answerLetterCount(answer), [answer]);
  const [values, setValues] = useState(() => Array(slotCount).fill(""));
  const [rowState, setRowState] = useState("idle");
  const inputRefs = useRef([]);

  useImperativeHandle(ref, () => ({
    focusFirst() {
      inputRefs.current[0]?.focus();
    },
  }));

  useEffect(() => {
    if (done) {
      setRowState("correct");
      const letters = String(answer).replace(/[^a-zA-Z0-9]/gi, "").split("");
      setValues(letters.map((c) => c.toUpperCase()).slice(0, slotCount));
    }
  }, [done, answer, slotCount]);

  const checkAnswer = useCallback(
    (nextValues) => {
      const filled = nextValues.every((v) => String(v).trim() !== "");
      if (!filled) {
        setRowState("idle");
        return;
      }
      const guess = joinSlotValues(nextValues);
      if (answersMatch(guess, answer)) {
        setRowState("correct");
        onSolved(index);
      } else {
        setRowState("wrong");
      }
    },
    [answer, index, onSolved]
  );

  const handleChange = (slotIdx, raw) => {
    if (done || rowState === "correct") return;
    const ch = String(raw).slice(-1).toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (rowState === "wrong") setRowState("idle");
    setValues((prev) => {
      const next = [...prev];
      next[slotIdx] = ch;
      checkAnswer(next);
      return next;
    });
    if (ch && slotIdx < slotCount - 1) {
      inputRefs.current[slotIdx + 1]?.focus();
    }
  };

  const handleKeyDown = (slotIdx, e) => {
    if (e.key === "Backspace" && !values[slotIdx] && slotIdx > 0) {
      inputRefs.current[slotIdx - 1]?.focus();
    }
  };

  return (
    <div
      className={`unscramble-stage__row${
        rowState === "correct" || done ? " unscramble-stage__row--done" : ""
      }`}
    >
      <div className="unscramble-stage__scrambled" style={textStyle}>
        <span className="unscramble-stage__scrambled-num" style={textStyle}>
          {index + 1}.
        </span>
        {scrambled}
        {showClueButton && clue ? (
          <button
            type="button"
            className="unscramble-stage__clue-btn"
            title="Show clue"
            onClick={() => onShowClue?.(clue)}
            aria-label={`Clue for word ${index + 1}`}
          >
            ℹ
          </button>
        ) : null}
      </div>

      <div className="unscramble-stage__tiles">
        {Array.from({ length: slotCount }, (_, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="text"
            maxLength={1}
            className={`unscramble-stage__tile${
              rowState === "correct" ? " unscramble-stage__tile--correct" : ""
            }${rowState === "wrong" ? " unscramble-stage__tile--wrong" : ""}`}
            value={values[i] || ""}
            disabled={done || rowState === "correct"}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            aria-label={`Letter ${i + 1} for word ${index + 1}`}
          />
        ))}
      </div>

      <span
        className={`unscramble-stage__check${
          rowState === "correct" || done
            ? " unscramble-stage__check--visible"
            : ""
        }`}
        aria-hidden
      >
        ✓
      </span>
    </div>
  );
});

export default UnscrambleRow;
